import { z } from 'zod';

import {
  AnswerPayloadSchema,
  CartIntervalSelectionSchema,
  CheckoutCompleteSchema,
  CheckoutExpressShippingSchema,
  CheckoutItemWithoutTaxSchema,
  CheckoutItemsOnlySchema,
  CheckoutSchema,
  CustomerNameSchema,
  ElementEventPayloadSchema,
  IdPayloadSchema,
  LabelPayloadSchema,
  ListItemSchema,
  MarketingSchema,
  NoPayloadSchema,
  PaymentChannelSchema,
  ProductListItemSchema,
  ProductListItemWithVariantSchema,
  ProductSchema,
  ProductWithTabSchema,
  ProductWithVariantSchema,
  SubscriptionItemPayloadSchema,
  SubscriptionLabelPayloadSchema,
  TitlePayloadSchema,
} from './commons';
import {
  AccountEventName,
  CartEventName,
  CheckoutEventName,
  CheckoutExpressEventName,
  CheckoutQuestionnaireEventName,
  CheckoutWalletEventName,
  CollectionEventName,
  CommonEventName,
  ComponentEventName,
  ConsentEventName,
  FuBeEventName,
  GroupName,
  LoyaltyEventName,
  PickyCatsEventName,
  ProductEventName,
  PromoBarEventName,
  StandaloneEventName,
  StoryEventName,
  SubscriptionEventName,
} from './event-names';

const CartReadyPayloadSchema = z
  .object({
    checkout: CheckoutItemsOnlySchema,
  })
  .strict();

const CartSelectCheckoutMethodSchema = z.union([
  z
    .object({
      type: z.literal('standard'),
      checkout: CheckoutItemsOnlySchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('express'),
      payment: PaymentChannelSchema,
      checkout: CheckoutItemsOnlySchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('wallet'),
      payment: PaymentChannelSchema,
      checkout: CheckoutItemsOnlySchema,
    })
    .strict(),
]);

const CheckoutRoleSchema = z
  .object({
    type: z.enum(['signup', 'login', 'guest', 'otp']),
  })
  .strict();

const CheckoutStandardShippingSelectionSchema = z
  .object({
    type: z.string(),
    checkout: CheckoutItemsOnlySchema,
  })
  .strict();

const CheckoutPaymentSelectionSchema = z
  .object({
    payment: PaymentChannelSchema,
  })
  .strict();

const CheckoutPaymentWithItemsSchema = CheckoutPaymentSelectionSchema.extend({
  checkout: CheckoutItemsOnlySchema,
}).strict();

const CheckoutStandardSubmitAddressesSchema = z
  .object({
    customer: CustomerNameSchema,
    checkout: CheckoutItemsOnlySchema,
  })
  .strict();

const CheckoutExpressReadySummarySchema = z
  .object({
    customer: CustomerNameSchema,
    payment: PaymentChannelSchema,
    checkout: CheckoutItemsOnlySchema,
  })
  .strict();

const CheckoutExpressSubmitOrderSchema = z
  .object({
    checkout: CheckoutItemsOnlySchema,
    payment: PaymentChannelSchema,
    shipping: CheckoutExpressShippingSchema,
  })
  .strict();

const CheckoutPurchasePayloadSchema = z
  .object({
    checkout: CheckoutCompleteSchema,
  })
  .merge(MarketingSchema);

const CheckoutQuestionnaireAnswerSchema = AnswerPayloadSchema;

const ProductClickPayloadSchema = z.union([
  z.object({ product: ProductListItemSchema }).strict(),
  z.object({ product: ProductSchema }).strict(),
]);

const ProductImpressionsPayloadSchema = z
  .object({
    products: z.array(ProductListItemSchema),
  })
  .strict();

const ProductDetailsPayloadSchema = z
  .object({
    product: ProductListItemWithVariantSchema,
  })
  .strict();

const ProductSwitchVariantPayloadSchema = z
  .object({
    product: ProductWithVariantSchema,
  })
  .strict();

const ProductAddToCartPayloadSchema = z
  .object({
    list: z.string().optional(),
    item: CheckoutItemWithoutTaxSchema,
  })
  .strict();

const ProductInfoTabClickPayloadSchema = z
  .object({
    product: ProductWithTabSchema,
  })
  .strict();

const ComponentClickPayloadSchema = z
  .object({
    label: z.string(),
    group: z.string().optional(),
  })
  .strict();

const CollectionReadyPayloadSchema = z
  .object({
    products: z.array(ProductListItemWithVariantSchema),
  })
  .strict();

const FuBeQuestionPayloadSchema = z
  .object({
    key: z.string(),
    step: z.number(),
  })
  .strict();

const FuBeAnswerPayloadSchema = z
  .object({
    key: z.string(),
    step: z.number(),
    value: z.string(),
  })
  .strict();

const LoyaltyRedeemPayloadSchema = z
  .object({
    value: z.number(),
  })
  .strict();

const CountryModalClickedPayloadSchema = z
  .object({
    targetShop: z.union([z.boolean(), z.string()]),
  })
  .strict();

const AnyPayloadSchema = z.record(z.string(), z.any()).optional();

export const StandaloneEventPayloadSchemas = {
  [StandaloneEventName.PageView]: NoPayloadSchema,
  [StandaloneEventName.SelectElement]: ElementEventPayloadSchema,
  [StandaloneEventName.UnselectElement]: ElementEventPayloadSchema,
  [StandaloneEventName.ViewElement]: ElementEventPayloadSchema,
  [StandaloneEventName.ScrollElement]: ElementEventPayloadSchema,
  [StandaloneEventName.ZoomElement]: ElementEventPayloadSchema,

  // Legacy events; moved from utils/tracking.ts
  [StandaloneEventName.CookieConsentAccepted]: NoPayloadSchema,
  [StandaloneEventName.CookieConsentDismissed]: NoPayloadSchema,
  [StandaloneEventName.CookieConsentDisplay]: NoPayloadSchema,
  [StandaloneEventName.CountryModalClicked]: CountryModalClickedPayloadSchema,
  [StandaloneEventName.CountryModalDismissed]: NoPayloadSchema,
  [StandaloneEventName.NextJsMetric]: AnyPayloadSchema,
  [StandaloneEventName.NewsletterSignup]: NoPayloadSchema,
  [StandaloneEventName.ReorderDetail]: NoPayloadSchema,
  [StandaloneEventName.ReorderMyAccount]: NoPayloadSchema,
  [StandaloneEventName.SearchStart]: NoPayloadSchema,
  [StandaloneEventName.WebVitals]: AnyPayloadSchema,
} as const;

const accountEventPayloadSchemas = {
  [CommonEventName.Impression]: LabelPayloadSchema,
  [AccountEventName.Login]: NoPayloadSchema,
  [AccountEventName.Logout]: NoPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const adTileEventPayloadSchemas = {
  [CommonEventName.Click]: z.object({ click: ListItemSchema }).strict(),
  [CommonEventName.Impression]: z.object({ impression: ListItemSchema }).strict(),
} as const satisfies Record<string, z.ZodTypeAny>;

const cartEventPayloadSchemas = {
  [CartEventName.ReadyCart]: CartReadyPayloadSchema,
  [CartEventName.OpenSubscriptionOptions]: NoPayloadSchema,
  [CartEventName.SelectIntervalOption]: CartIntervalSelectionSchema,
  [CartEventName.CheckoutOptionSelect]: CartSelectCheckoutMethodSchema,
} as const satisfies Record<CartEventName, z.ZodTypeAny>;

const checkoutExpressEventPayloadSchemas = {
  [CheckoutExpressEventName.ReadySummary]: CheckoutExpressReadySummarySchema,
  [CheckoutExpressEventName.SelectShippingOption]: CheckoutExpressShippingSchema,
  [CheckoutExpressEventName.SubmitOrder]: CheckoutExpressSubmitOrderSchema,
  [CheckoutExpressEventName.ReadyConfirmation]: CheckoutPaymentSelectionSchema,
  [CheckoutExpressEventName.Purchase]: CheckoutPurchasePayloadSchema,
  [CheckoutExpressEventName.Confirm]: NoPayloadSchema,
} as const satisfies Record<CheckoutExpressEventName, z.ZodTypeAny>;

const checkoutWalletEventPayloadSchemas = {
  [CheckoutWalletEventName.SelectShippingOption]: CheckoutExpressShippingSchema,
  [CheckoutWalletEventName.SelectShippingAddress]: NoPayloadSchema,
  [CheckoutWalletEventName.PaymentSuccess]: NoPayloadSchema,
  [CheckoutWalletEventName.OrderSuccess]: NoPayloadSchema,
} as const satisfies Record<CheckoutWalletEventName, z.ZodTypeAny>;

const checkoutStandardEventPayloadSchemas = {
  [CheckoutEventName.CheckoutLoaded]: z
    .object({
      checkout: CheckoutSchema,
    })
    .strict(),
  [CheckoutEventName.ReadyUserRole]: NoPayloadSchema,
  [CheckoutEventName.SelectUserRole]: CheckoutRoleSchema,
  [CheckoutEventName.SubmitUserRole]: CheckoutRoleSchema,
  [CheckoutEventName.UserIdentified]: CheckoutRoleSchema.extend({
    checkout: CheckoutItemsOnlySchema,
  }).strict(),
  [CheckoutEventName.ReadyAddresses]: NoPayloadSchema,
  [CheckoutEventName.SubmitAddresses]: CheckoutStandardSubmitAddressesSchema,
  [CheckoutEventName.ReadyShipping]: NoPayloadSchema,
  [CheckoutEventName.SelectShippingOption]: CheckoutStandardShippingSelectionSchema,
  [CheckoutEventName.SubmitShipping]: CheckoutStandardShippingSelectionSchema,
  [CheckoutEventName.ReadyPayment]: NoPayloadSchema,
  [CheckoutEventName.SelectPaymentOption]: CheckoutPaymentSelectionSchema,
  [CheckoutEventName.SubmitPayment]: CheckoutPaymentWithItemsSchema,
  [CheckoutEventName.ReadyConfirmation]: CheckoutPaymentSelectionSchema,
  [CheckoutEventName.Purchase]: CheckoutPurchasePayloadSchema,
} as const satisfies Record<CheckoutEventName, z.ZodTypeAny>;

const checkoutQuestionnaireEventPayloadSchemas = {
  [CheckoutQuestionnaireEventName.Start]: NoPayloadSchema,
  [CheckoutQuestionnaireEventName.View]: NoPayloadSchema,
  [CheckoutQuestionnaireEventName.End]: NoPayloadSchema,
  [CheckoutQuestionnaireEventName.Answer]: CheckoutQuestionnaireAnswerSchema,
} as const satisfies Record<CheckoutQuestionnaireEventName, z.ZodTypeAny>;

const collectionEventPayloadSchemas = {
  [CommonEventName.Ready]: CollectionReadyPayloadSchema,
  [CollectionEventName.FilterOptionsOpen]: NoPayloadSchema,
  [CollectionEventName.FilterOptionsConfirm]: NoPayloadSchema,
  [CollectionEventName.FilterOptionsClear]: NoPayloadSchema,
  [CollectionEventName.FilterOptionsReset]: NoPayloadSchema,
  [CollectionEventName.FilterOptionsCancel]: NoPayloadSchema,
  [CollectionEventName.FilterOptionsClick]: LabelPayloadSchema,
  [CollectionEventName.SortOptionsOpen]: NoPayloadSchema,
  [CollectionEventName.SortOptionsSelect]: LabelPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const componentEventPayloadSchemas = {
  [ComponentEventName.Click]: ComponentClickPayloadSchema,
  [ComponentEventName.Impression]: LabelPayloadSchema,
} as const satisfies Record<ComponentEventName, z.ZodTypeAny>;

const consentEventPayloadSchemas = {
  [ConsentEventName.Ok]: NoPayloadSchema,
  [ConsentEventName.Settings]: NoPayloadSchema,
  [ConsentEventName.AcceptAll]: NoPayloadSchema,
  [ConsentEventName.ConfirmSelection]: LabelPayloadSchema,
} as const satisfies Record<ConsentEventName, z.ZodTypeAny>;

const conversationalWidgetEventPayloadSchemas = {
  [CommonEventName.Click]: LabelPayloadSchema,
  [CommonEventName.Impression]: NoPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const debugEventPayloadSchemas = {
  'Experiment Loaded': LabelPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const formEventPayloadSchemas = {
  [CommonEventName.Submit]: LabelPayloadSchema,
} as const;

const fuBeEventPayloadSchemas = {
  [FuBeEventName.Question]: FuBeQuestionPayloadSchema,
  [FuBeEventName.Answer]: FuBeAnswerPayloadSchema,
} as const satisfies Record<FuBeEventName, z.ZodTypeAny>;

const loyaltyEventPayloadSchemas = {
  [LoyaltyEventName.RedeemOptionView]: NoPayloadSchema,
  [LoyaltyEventName.Redeem]: LoyaltyRedeemPayloadSchema,
  [CommonEventName.Impression]: LabelPayloadSchema,
  [CommonEventName.Click]: LabelPayloadSchema,
  [CommonEventName.Success]: LabelPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const productEventPayloadSchemas = {
  [ProductEventName.Impressions]: ProductImpressionsPayloadSchema,
  [ProductEventName.Click]: ProductClickPayloadSchema,
  [ProductEventName.Details]: ProductDetailsPayloadSchema,
  [ProductEventName.SwitchVariant]: ProductSwitchVariantPayloadSchema,
  [ProductEventName.AddToCart]: ProductAddToCartPayloadSchema,
  [ProductEventName.InfoTabClick]: ProductInfoTabClickPayloadSchema,
  [ProductEventName.ClickAdventCalendar]: LabelPayloadSchema,
} as const satisfies Record<ProductEventName, z.ZodTypeAny>;

const promoBarEventPayloadSchemas = {
  [PromoBarEventName.SubmitCouponCode]: NoPayloadSchema,
  [CommonEventName.Click]: IdPayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const PickyCatsJourneyAttributionSchema = z
  .object({
    variant: z.enum(['V1', 'V2', 'V3']),
    variantIndex: z.number().int().nonnegative().optional(),
    assignmentSource: z.enum(['default', 'experiment']),
  })
  .strict();

const pickyCatsJourneyAnswerSteps = [
  'serving-temperature',
  'bowl-shape',
  'feeding-frequency',
  'bowl-location',
  'food-type',
  'flavour',
  'age-band',
  'cat-count',
  'education-result',
] as const;

const pickyCatsJourneyExitSteps = [...pickyCatsJourneyAnswerSteps, 'recommendation'] as const;

const pickyCatsEventPayloadSchemas = {
  [PickyCatsEventName.JourneyEntry]: PickyCatsJourneyAttributionSchema.extend({
    action: z.enum(['view', 'select']),
    location: z.string().min(1),
  }).strict(),
  [PickyCatsEventName.JourneyAnswer]: PickyCatsJourneyAttributionSchema.extend({
    step: z.enum(pickyCatsJourneyAnswerSteps),
    answer: z.string().min(1),
  }).strict(),
  [PickyCatsEventName.JourneyResult]: PickyCatsJourneyAttributionSchema.extend({
    status: z.enum(['view', 'success', 'empty', 'error']),
    productCount: z.number().int().nonnegative().optional(),
  }).strict(),
  [PickyCatsEventName.JourneyHandoff]: PickyCatsJourneyAttributionSchema,
  [PickyCatsEventName.JourneyExit]: PickyCatsJourneyAttributionSchema.extend({
    lastStep: z.enum(pickyCatsJourneyExitSteps),
  }).strict(),
  [PickyCatsEventName.CartAction]: PickyCatsJourneyAttributionSchema.extend({
    status: z.enum(['click', 'success', 'error']),
    productId: z.string().min(1),
    variantId: z.string().min(1),
    quantity: z.number().int().positive(),
    mode: z.literal('one-off'),
  }).strict(),
} as const satisfies Record<PickyCatsEventName, z.ZodTypeAny>;

const promoHeaderEventPayloadSchemas = {
  [CommonEventName.Click]: TitlePayloadSchema,
  [CommonEventName.View]: TitlePayloadSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

const storyEventPayloadSchemas = {
  [StoryEventName.Impressions]: LabelPayloadSchema,
  [StoryEventName.Views]: LabelPayloadSchema,
  [StoryEventName.Next]: LabelPayloadSchema,
  [StoryEventName.Previous]: LabelPayloadSchema,
  [StoryEventName.Exit]: LabelPayloadSchema,
} as const satisfies Record<StoryEventName, z.ZodTypeAny>;

const subscriptionEventPayloadSchemas = {
  [SubscriptionEventName.MoveOptionsView]: NoPayloadSchema,
  [SubscriptionEventName.MoveOptionsConfirm]: NoPayloadSchema,
  [SubscriptionEventName.PauseOptionsView]: NoPayloadSchema,
  [SubscriptionEventName.PauseOptionsPauseSelect]: NoPayloadSchema,
  [SubscriptionEventName.PauseOptionsPauseConfirm]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.PauseOptionsMoveConfirm]: NoPayloadSchema,
  [SubscriptionEventName.CancelOptionsView]: NoPayloadSchema,
  [SubscriptionEventName.CancelOptionsConfirm]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.Restart]: NoPayloadSchema,
  [SubscriptionEventName.AddItem]: SubscriptionItemPayloadSchema,
  [SubscriptionEventName.RemoveItem]: SubscriptionItemPayloadSchema,
  [SubscriptionEventName.RestoreItem]: SubscriptionItemPayloadSchema,
  [SubscriptionEventName.IncreaseItemQuantity]: NoPayloadSchema,
  [SubscriptionEventName.DecreaseItemQuantity]: NoPayloadSchema,
  [SubscriptionEventName.SelectFilter]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.IntervalSelect]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.OrderTypeSelect]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.AddToSubscriptionClick]: NoPayloadSchema,
  [SubscriptionEventName.UpsellListView]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.UpsellListSelect]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.UpsellListClickItem]: SubscriptionLabelPayloadSchema,
  [SubscriptionEventName.UpsellListModalView]: NoPayloadSchema,
  [SubscriptionEventName.UpsellListAddItem]: SubscriptionLabelPayloadSchema,
} as const satisfies Record<SubscriptionEventName, z.ZodTypeAny>;

export const GroupEventPayloadSchemas = {
  [GroupName.Account]: accountEventPayloadSchemas,
  [GroupName.AdTile]: adTileEventPayloadSchemas,
  [GroupName.Cart]: cartEventPayloadSchemas,
  [GroupName.CheckoutExpress]: checkoutExpressEventPayloadSchemas,
  [GroupName.CheckoutWallet]: checkoutWalletEventPayloadSchemas,
  [GroupName.CheckoutStandard]: checkoutStandardEventPayloadSchemas,
  [GroupName.CheckoutQuestionnaire]: checkoutQuestionnaireEventPayloadSchemas,
  [GroupName.Collection]: collectionEventPayloadSchemas,
  [GroupName.Component]: componentEventPayloadSchemas,
  [GroupName.Consent]: consentEventPayloadSchemas,
  [GroupName.ConversationalWidget]: conversationalWidgetEventPayloadSchemas,
  [GroupName.Debug]: debugEventPayloadSchemas,
  [GroupName.Form]: formEventPayloadSchemas,
  [GroupName.FuBe]: fuBeEventPayloadSchemas,
  [GroupName.Loyalty]: loyaltyEventPayloadSchemas,
  [GroupName.Product]: productEventPayloadSchemas,
  [GroupName.PromoBar]: promoBarEventPayloadSchemas,
  [GroupName.PickyCats]: pickyCatsEventPayloadSchemas,
  [GroupName.PromoHeader]: promoHeaderEventPayloadSchemas,
  [GroupName.Story]: storyEventPayloadSchemas,
  [GroupName.Subscription]: subscriptionEventPayloadSchemas,
  [GroupName.Customer]: {} as const,
  [GroupName.Shipping]: {} as const,
  [GroupName.HomePromoTile]: {} as const,
} as const satisfies Record<GroupName, Readonly<Record<string, z.ZodTypeAny>>>;

export type StandaloneEventPayload<Name extends StandaloneEventName> = z.input<
  (typeof StandaloneEventPayloadSchemas)[Name]
>;

export type GroupEventName<Group extends GroupName> = {
  [G in Group]: Extract<keyof (typeof GroupEventPayloadSchemas)[G], string>;
}[Group];

export type GroupEventPayload<
  Group extends GroupName,
  Name extends GroupEventName<Group>,
> = z.input<(typeof GroupEventPayloadSchemas)[Group][Name]>;

export type ParsedGroupEventPayload<
  Group extends GroupName,
  Name extends GroupEventName<Group>,
> = z.output<(typeof GroupEventPayloadSchemas)[Group][Name]>;

export type ParsedStandaloneEventPayload<Name extends StandaloneEventName> = z.output<
  (typeof StandaloneEventPayloadSchemas)[Name]
>;

export const getStandaloneEventSchema = <Name extends StandaloneEventName>(name: Name) =>
  StandaloneEventPayloadSchemas[name];

export const getGroupEventSchema = <Group extends GroupName, Name extends GroupEventName<Group>>(
  group: Group,
  name: Name
) => GroupEventPayloadSchemas[group][name];

export const parseStandaloneEventPayload = <Name extends StandaloneEventName>(
  name: Name,
  payload: unknown
): ParsedStandaloneEventPayload<Name> => {
  const schema = getStandaloneEventSchema(name);
  return schema.parse(payload) as ParsedStandaloneEventPayload<Name>;
};

export const parseGroupEventPayload = <Group extends GroupName, Name extends GroupEventName<Group>>(
  group: Group,
  name: Name,
  payload: unknown
): ParsedGroupEventPayload<Group, Name> => {
  const schema = getGroupEventSchema(group, name) as z.ZodTypeAny;
  return schema.parse(payload) as ParsedGroupEventPayload<Group, Name>;
};

export const isStandaloneEventName = (value: unknown): value is StandaloneEventName =>
  typeof value === 'string' &&
  Object.values(StandaloneEventName).includes(value as StandaloneEventName);

export const isGroupEventName = <Group extends GroupName>(
  group: Group,
  value: unknown
): value is GroupEventName<Group> =>
  typeof value === 'string' && value in GroupEventPayloadSchemas[group];
